import { inject, Injectable } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import {
  Auth,
  onAuthStateChanged,
  onIdTokenChanged,
  User as FirebaseUser,
} from '@angular/fire/auth';
import { filter, firstValueFrom, ReplaySubject, take } from 'rxjs';

import { SmartOrpcClientService } from './smart-orpc-client.service';
import { UserRole, UserStatus } from '#backend/common/user-types';

import { environment } from '../../../environments/environment';

@Injectable({
  providedIn: 'root',
})
export class UserService {
  private auth = inject(Auth);
  private orpc = inject(SmartOrpcClientService);

  private userSubject = new ReplaySubject<AuthenticatedUser | null>(1);
  private authenticationSubject = new ReplaySubject<boolean>(1);
  private authTokenSubject = new ReplaySubject<string | null>(1);

  readonly user$ = this.userSubject.asObservable();
  readonly authentication$ = this.authenticationSubject.asObservable();
  readonly authToken$ = this.authTokenSubject.asObservable();

  readonly user = toSignal(this.user$, { initialValue: null });

  constructor() {
    this.userSubject.next(null);
    this.initializeAuthState();
  }

  get role(): UserRole | null {
    if (environment.skipAuth) return UserRole.ADMIN_GEOAMBIENTE;
    return this.user()?.role ?? null;
  }

  get isGeoambiente(): boolean {
    if (environment.skipAuth) return true;
    const role = this.role;
    return (
      role === UserRole.ADMIN_GEOAMBIENTE ||
      role === UserRole.OPERADOR_GEOAMBIENTE
    );
  }

  get canManageClient(): boolean {
    const role = this.role;
    return role === UserRole.ADMIN_CLIENTE;
  }

  get username(): string {
    if (environment.skipAuth) {
      return 'Developer (Debug Mode)';
    }

    return this.user()?.name ?? this.auth.currentUser?.displayName ?? 'Usuário';
  }

  async requireUser(): Promise<AuthenticatedUser> {
    const currentUser = this.user();
    if (currentUser) {
      return currentUser;
    }

    const userPromise = firstValueFrom(
      this.user$.pipe(
        filter((value): value is AuthenticatedUser => value !== null),
        take(1),
      ),
    );

    const authFailurePromise = firstValueFrom(
      this.authentication$.pipe(
        filter((isAuthenticated) => isAuthenticated === false),
        take(1),
      ),
    ).then(() => {
      throw new Error('Usuário não autenticado');
    });

    return await Promise.race([userPromise, authFailurePromise]);
  }

  private async buildUserFromClaims(
    firebaseUser: FirebaseUser,
  ): Promise<AuthenticatedUser> {
    const tokenResult = await firebaseUser.getIdTokenResult(true);
    const claims = tokenResult.claims as SessionClaims;

    const role = claims.role as UserRole | undefined;
    const userIdRaw = claims.userId;

    if (!role || userIdRaw === undefined || userIdRaw === null) {
      throw new Error('Token sem permissões válidas');
    }

    const id = userIdRaw;
    if (id === null) {
      throw new Error('Identificador de usuário inválido nas claims');
    }

    const organizationIdRaw = claims.organizationId;
    let organizationId: number | null = null;
    if (organizationIdRaw !== undefined && organizationIdRaw !== null) {
      const parsedOrganizationId = Number(organizationIdRaw);
      organizationId = Number.isFinite(parsedOrganizationId)
        ? parsedOrganizationId
        : null;
    }

    const email = firebaseUser.email ?? claims.email ?? '';

    const status = claims.status ?? UserStatus.ACTIVE;

    const organizationName = claims.organizationName ?? undefined;

    const lastAccess =
      typeof claims.lastAccess === 'string'
        ? new Date(claims.lastAccess)
        : null;

    const name = claims.name || firebaseUser.displayName || email || 'Usuário';

    const profile: AuthenticatedUser = {
      id,
      email,
      name,
      role,
      status,
      organizationId,
      organizationName,
      lastAccess,
    };

    this.userSubject.next(profile);
    return profile;
  }

  private initializeAuthState(): void {
    if (environment.skipAuth) {
      console.log(
        '⚠️  DEBUG MODE: Skipping Firebase authentication - using mock admin user',
      );

      this.userSubject.next({
        id: '0',
        name: 'Developer (Debug Mode)',
        email: 'dev@geoambiente.com.br',
        role: UserRole.ADMIN_GEOAMBIENTE,
        status: UserStatus.ACTIVE,
        organizationId: null,
        organizationName: 'Geoambiente (Debug)',
        lastAccess: new Date(),
      });
      this.authenticationSubject.next(true);
      return;
    }

    onAuthStateChanged(this.auth, async (firebaseUser) => {
      if (!firebaseUser) {
        this.userSubject.next(null);
        this.authenticationSubject.next(false);
        this.authTokenSubject.next(null);
        return;
      }

      try {
        await this.buildUserFromClaims(firebaseUser);
        this.authenticationSubject.next(true);
      } catch (error) {
        console.error('[Auth] Falha ao carregar perfil do usuário', error);
        this.userSubject.next(null);
        this.authenticationSubject.next(false);
        this.authTokenSubject.next(null);
        await this.auth.signOut();
      }
    });

    onIdTokenChanged(this.auth, async (firebaseUser) => {
      const token = await firebaseUser?.getIdToken();
      this.authTokenSubject.next(token ?? null);
    });

    // Se a máquina suspende (sleep) ou a aba fica em background, o timer que
    // o Firebase agenda para renovar o token antes de expirar pode não
    // disparar (setTimeout não roda com a aba/máquina suspensa). Ao voltar a
    // ficar visível, getIdToken() (sem forceRefresh) só bate na rede se o
    // token já expirou ou expira nos próximos 5 minutos — então não gera
    // custo extra quando o token ainda está válido, e fecha a janela antes do
    // usuário mandar a próxima requisição.
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        this.auth.currentUser?.getIdToken();
      }
    });
  }

  async refreshUser(): Promise<AuthenticatedUser | null> {
    if (!this.auth.currentUser) {
      this.userSubject.next(null);
      this.authenticationSubject.next(false);
      this.authTokenSubject.next(null);
      return null;
    }

    try {
      return this.buildUserFromClaims(this.auth.currentUser);
    } catch (error) {
      console.error('[Auth] Falha ao atualizar perfil do usuário', error);
      return null;
    }
  }

  async signOut() {
    if (environment.skipAuth) {
      console.log('⚠️ DEBUG MODE: Sign out ignored in debug mode');
      return;
    }

    // Antes de derrubar a sessão do Firebase (que invalida o token usado no
    // header Authorization): expira o cookie __session, senão o manual de
    // instruções continuaria acessível no mesmo navegador após o logout.
    await this.orpc.safeClient.instructionManual.endSession();

    await this.auth.signOut();
    this.userSubject.next(null);
    this.authenticationSubject.next(false);
    this.authTokenSubject.next(null);
  }
}

interface SessionClaims {
  role?: UserRole;
  organizationId?: number | string;
  organizationName?: string | null;
  userId?: string;
  name?: string;
  status?: UserStatus;
  lastAccess?: string;
  email?: string;
}

export interface AuthenticatedUser {
  id: string;
  email: string;
  name: string;
  role: UserRole;
  status: UserStatus;
  organizationId: number | null;
  organizationName?: string;
  lastAccess: Date | null;
}
